import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useApi } from '@/services/api';
import { Plus, Briefcase, Trash2 } from 'lucide-react';

export function ProjectsPage() {
  const { workspaceId } = useParams<{ workspaceId: string }>();
  const api = useApi();
  const queryClient = useQueryClient();
  const [isCreating, setIsCreating] = useState(false);
  const [newProject, setNewProject] = useState({ name: '', identifier: '' });

  const { data: projects, isLoading } = useQuery({
    queryKey: ['projects', workspaceId],
    queryFn: () => api.getProjects(workspaceId!),
    enabled: !!workspaceId,
  });

  const createMutation = useMutation({
    mutationFn: async (data: { name: string; identifier: string }) => {
      console.log('mutationFn called with:', data);
      const result = await api.createProject({
        bh_name: data.name,
        bh_identifier: data.identifier.toUpperCase(),
        bh_workspace: workspaceId,
      });
      console.log('createProject result:', result);
      return result;
    },
    onSuccess: (data) => {
      console.log('onSuccess called', data);
      queryClient.invalidateQueries({ queryKey: ['projects', workspaceId] });
      setIsCreating(false);
      setNewProject({ name: '', identifier: '' });
    },
    onError: (error) => {
      console.error('onError called:', error);
      alert('Failed to create project: ' + (error as Error).message);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.deleteProject(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['projects', workspaceId] });
    },
  });

  const handleCreate = (e: React.FormEvent) => {
    e.preventDefault();
    console.log('handleCreate called', newProject);
    if (newProject.name.trim() && newProject.identifier.trim()) {
      console.log('Calling createMutation.mutate');
      createMutation.mutate(newProject);
    } else {
      console.log('Validation failed - name or identifier empty');
    }
  };

  if (isLoading) {
    return (
      <div className="p-6 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Projects</h1>
        <button
          onClick={() => setIsCreating(true)}
          className="flex items-center gap-2 bg-primary-600 text-white px-4 py-2 rounded-lg hover:bg-primary-700 transition-colors"
        >
          <Plus className="w-4 h-4" />
          New Project
        </button>
      </div>

      {isCreating && (
        <form onSubmit={handleCreate} className="mb-6 bg-white p-4 rounded-lg border border-gray-200">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Project Name
              </label>
              <input
                type="text"
                value={newProject.name}
                onChange={(e) => setNewProject({ ...newProject, name: e.target.value })}
                placeholder="My Project"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                autoFocus
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Identifier
              </label>
              <input
                type="text"
                value={newProject.identifier}
                onChange={(e) => setNewProject({ ...newProject, identifier: e.target.value.toUpperCase().slice(0, 5) })}
                placeholder="PROJ"
                maxLength={5}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 uppercase"
              />
            </div>
          </div>
          <div className="flex gap-2 mt-4">
            <button
              type="submit"
              disabled={createMutation.isPending}
              className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
            >
              {createMutation.isPending ? 'Creating...' : 'Create Project'}
            </button>
            <button
              type="button"
              onClick={() => setIsCreating(false)}
              className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg"
            >
              Cancel
            </button>
          </div>
        </form>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {projects?.map((project: any) => (
          <Link
            key={project.bh_projectid}
            to={`/workspaces/${workspaceId}/projects/${project.bh_projectid}/board`}
            className="bg-white p-4 rounded-lg border border-gray-200 hover:border-primary-300 hover:shadow-md transition-all group"
          >
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-accent-100 rounded-lg flex items-center justify-center">
                  <Briefcase className="w-5 h-5 text-accent-600" />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-mono bg-gray-100 px-2 py-0.5 rounded text-gray-600">
                      {project.bh_identifier}
                    </span>
                  </div>
                  <h3 className="font-medium text-gray-900 mt-1">{project.bh_name}</h3>
                </div>
              </div>
              <button
                onClick={(e) => {
                  e.preventDefault();
                  if (confirm('Delete this project?')) {
                    deleteMutation.mutate(project.bh_projectid);
                  }
                }}
                className="p-2 text-gray-400 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          </Link>
        ))}

        {projects?.length === 0 && !isCreating && (
          <div className="col-span-full text-center py-12">
            <Briefcase className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">No projects yet</p>
            <button
              onClick={() => setIsCreating(true)}
              className="mt-4 text-primary-600 hover:text-primary-700 font-medium"
            >
              Create your first project
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
